import { TextoCortado } from '../../componentes/texto-cortado';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, CalendarDays, ChevronLeft, ChevronRight, Plus, Users } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Drawer } from '../../componentes/sobreposicao';
import { getTodos } from '../../nucleo/api';
import { hora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { DialogoConcluir } from './ConcluirAtividades';
import { CAMINHO_ATIVIDADES, CAMINHO_SEM_TAREFA, caminhoDaReuniaoNaFicha, caminhoDaTarefa, caminhoDoAgendamento } from './crm-caminhos';
import { FormTarefa, rascunhoInicial } from './FormTarefa';
import { ProximaTarefa, useWorkflows } from './ProximaTarefa';
import { ESTADOS_REUNIAO_ABERTA, reunioesSemAtividade, tituloDaReuniao, useVeReunioes, type ReuniaoAgenda } from './reunioes-agenda';
import { IconeTipoAtividade, juntarDataHora } from './tarefas-comum';
import { TarefasExcluidasGeral } from './TarefasExcluidas';
import type { Atividade } from './tipos';

/** "novembro de 2026" vira "Novembro de 2026": só a primeira letra sobe (o `capitalize` do CSS subia também o "De"). */
export function tituloDoMes(dia: Date, idioma: string): string {
  const texto = dia.toLocaleDateString(idioma, { month: 'long', year: 'numeric' });
  return texto.charAt(0).toLocaleUpperCase(idioma) + texto.slice(1);
}
/** Início do dia local. */
export const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Os 7 dias (segunda a domingo) da semana do dia, como a faixa da origem. Função pura, testada. */
export function semanaDoDia(dia: Date): Date[] {
  const base = inicioDoDia(dia);
  const segunda = new Date(base.getFullYear(), base.getMonth(), base.getDate() - ((base.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => new Date(segunda.getFullYear(), segunda.getMonth(), segunda.getDate() + i));
}

/**
 * Intervalo do dia local em ISO, para o `GET /pipeline/atividades`. Review rcrm3 robustez (B3): o `ate` com data e hora é
 * inclusivo na API, então vai até 1 ms antes da meia-noite seguinte (a tarefa das 00:00 é só do dia dela).
 */
export function intervaloDoDia(dia: Date): { de: string; ate: string } {
  const de = inicioDoDia(dia);
  return { de: de.toISOString(), ate: new Date(new Date(de.getFullYear(), de.getMonth(), de.getDate() + 1).getTime() - 1).toISOString() };
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Lista B de 30/10 (item 27): item do atalho, tarefa do Pipeline ou reunião da Agenda (a que ainda não é uma tarefa). */
export type ItemDoAtalho = { tipo: 'tarefa'; a: Atividade; quando: string } | { tipo: 'reuniao'; r: ReuniaoAgenda; quando: string };

/**
 * Junta tarefas pendentes e reuniões abertas do dia numa lista por horário. A reunião que já é uma atividade (item 23,
 * `agendamento_id`, mesmo concluída) aparece só como a tarefa. Função pura, testada.
 */
export function itensDoAtalho(pendentes: readonly Atividade[], todasDoDia: readonly Atividade[], reunioes: readonly ReuniaoAgenda[]): ItemDoAtalho[] {
  const abertas = reunioesSemAtividade(reunioes, todasDoDia).filter((r) => ESTADOS_REUNIAO_ABERTA.includes(r.estado));
  return [...pendentes.map((a) => ({ tipo: 'tarefa' as const, a, quando: a.data_hora })), ...abertas.map((r) => ({ tipo: 'reuniao' as const, r, quando: r.inicio }))].sort((x, y) => x.quando.localeCompare(y.quando));
}

const COR: Record<'atrasada' | 'hoje' | 'futura', CorSelo> = { atrasada: 'laranja', hoje: 'azul', futura: 'neutro' };

/**
 * Ajustes do CRM 27/10 (C2, C15, K3; decisão 11): "Atalho de tarefas" da origem. Drawer com o mês, a faixa da semana com
 * setas e "Ver hoje", e as tarefas pendentes do dia (o checkbox abre a conclusão com a ata de sempre). Por padrão só as do
 * usuário logado, com troca para todas. Rodapé: "Ver negociações sem tarefa" (filtro do Comercial) e "Criar tarefa".
 */
export function AtalhoTarefas({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t, i18n } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const workflows = useWorkflows();
  const [dia, setDia] = useState(() => inicioDoDia(new Date()));
  const [todas, setTodas] = useState(false);
  const [concluir, setConcluir] = useState<Atividade | null>(null);
  const [proxima, setProxima] = useState<{ a: Atividade; ata: string } | null>(null);
  const [criar, setCriar] = useState(false);
  const intervalo = intervaloDoDia(dia);
  const comReunioes = useVeReunioes();
  // Com a Agenda à vista a consulta traz todas as atividades do dia (a concluída também entra), só para não repetir a reunião
  // que já é uma tarefa; a lista mostra só as pendentes.
  const q = useQuery({
    queryKey: ['/pipeline/atividades', 'atalho', intervalo.de, todas ? 'todas' : s.usuario.id, comReunioes ? 'com-reunioes' : 'tarefas'],
    queryFn: () => getTodos<Atividade>('/pipeline/atividades', { filtro: comReunioes ? 'todas' : 'pendentes', ...intervalo, responsaveis: todas ? undefined : s.usuario.id }),
    enabled: aberto,
  });
  const qr = useQuery({
    queryKey: ['/agenda/agendamentos', 'atalho', intervalo.de, todas ? 'todas' : s.usuario.id],
    queryFn: () => getTodos<ReuniaoAgenda>('/agenda/agendamentos', { de: intervalo.de, ate: intervalo.ate, responsavel_usuario_ids: todas ? undefined : s.usuario.id }),
    enabled: aberto && comReunioes,
  });
  const paramsExcl = useMemo(() => ({ de: intervalo.de, ate: intervalo.ate, responsaveis: todas ? undefined : s.usuario.id }), [intervalo.de, intervalo.ate, todas, s.usuario.id]);
  const doDia = q.data?.itens ?? [];
  const lista = itensDoAtalho(
    doDia.filter((a) => a.estado === 'pendente'),
    doDia,
    comReunioes ? (qr.data?.itens ?? []) : [],
  );
  const hoje = inicioDoDia(new Date());
  const semana = semanaDoDia(dia);
  const mover = (dias: number) => setDia((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + dias));
  const mes = tituloDoMes(dia, i18n.language);
  const podeEditar = s.pode('pipeline.editar');
  const statusDa = (a: Atividade) => (a.atrasada ? 'atrasada' : inicioDoDia(new Date(a.data_hora)).getTime() === hoje.getTime() ? 'hoje' : 'futura');
  const ir = (para: string) => {
    aoFechar();
    navegar(para);
  };
  return (
    <>
      <Drawer
        aberto={aberto && !concluir && !proxima && !criar}
        aoFechar={aoFechar}
        titulo={t('crm.atalho.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => ir(CAMINHO_SEM_TAREFA)} data-sem-tarefa-atalho="">
              {t('crm.atalho.semTarefa')}
            </Botao>
            {s.pode('pipeline.criar') && (
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setCriar(true)} data-criar-tarefa-atalho="">
                {t('crm.atividades.criar')}
              </Botao>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4" data-atalho-tarefas="">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">{mes}</span>
            <button type="button" className="text-sm font-medium text-acento-forte hover:underline" onClick={() => setDia(hoje)} data-ver-hoje="">
              {t('crm.atalho.verHoje')}
            </button>
          </div>
          <div className="flex items-center gap-1" role="group" aria-label={t('crm.atalho.semana')}>
            <Botao variante="fantasma" tamanho="sm" icone={<ChevronLeft className="size-4" />} aria-label={t('crm.atalho.semanaAnterior')} onClick={() => mover(-7)} />
            <div className="grid flex-1 grid-cols-7 gap-1">
              {semana.map((d) => {
                const ativo = d.getTime() === dia.getTime();
                const ehHoje = d.getTime() === hoje.getTime();
                return (
                  <button
                    key={d.getTime()}
                    type="button"
                    aria-pressed={ativo}
                    aria-label={d.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })}
                    onClick={() => setDia(d)}
                    className={`flex flex-col items-center rounded-lg py-1.5 text-xs font-semibold uppercase ${ativo ? 'bg-acento-forte text-sup-1' : ehHoje ? 'text-acento-forte hover:bg-sup-3' : 'text-tinta-2 hover:bg-sup-3'}`}
                    data-dia-atalho={ymd(d)}
                  >
                    {d.toLocaleDateString(i18n.language, { weekday: 'short' }).replace('.', '')}
                    <span className="numero text-base font-bold normal-case">{d.getDate()}</span>
                  </button>
                );
              })}
            </div>
            <Botao variante="fantasma" tamanho="sm" icone={<ChevronRight className="size-4" />} aria-label={t('crm.atalho.proximaSemana')} onClick={() => mover(7)} />
          </div>
          <label className="flex min-h-8 items-center gap-2 text-sm">
            <input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} data-atalho-todas="" />
            {t('crm.atalho.todas')}
          </label>
          {q.isPending || (comReunioes && qr.isPending) ? (
            <p className="text-sm text-tinta-3">{t('crm.atalho.carregando')}</p>
          ) : lista.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-2 py-8 text-center" data-atalho-vazio="">
              <CalendarCheck className="size-16 text-tinta-3" strokeWidth={1.25} aria-hidden />
              <p className="font-semibold">{t('crm.atalho.vazioTitulo')}</p>
              <p className="text-sm text-tinta-2">{t('crm.atalho.vazioTexto')}</p>
              <Botao tamanho="sm" icone={<CalendarDays className="size-4" />} onClick={() => ir(CAMINHO_ATIVIDADES)} data-ir-tarefas="">
                {t('crm.atalho.irTarefas')}
              </Botao>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {lista.map((item) => {
                if (item.tipo === 'reuniao') {
                  const r = item.r;
                  const titulo = textoLivre(tituloDaReuniao(r) || t('crm.ficha.reuniaoAgenda.semAssunto'), r.parceiro_nome);
                  const para = r.oportunidade_id ? caminhoDaReuniaoNaFicha(r.oportunidade_id, r.id) : caminhoDoAgendamento(r.id);
                  const status = r.fim < new Date().toISOString() ? 'atrasada' : inicioDoDia(new Date(r.inicio)).getTime() === hoje.getTime() ? 'hoje' : 'futura';
                  return (
                    <li key={`ag-${r.id}`} className="flex items-center gap-3 rounded-xl border border-borda bg-sup-1 px-3 py-2.5" data-reuniao-atalho={r.id}>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                          <Users className="size-3.5 shrink-0 text-tinta-2" aria-hidden />
                          <Link to={para} onClick={aoFechar} title={`${t('crm.atalho.abrirReuniao')}: ${titulo}`} className="min-w-0 hover:underline" data-abrir-reuniao-atalho="">
                            <TextoCortado>{titulo}</TextoCortado>
                          </Link>
                        </span>
                        <span className="flex min-w-0 items-center gap-1 text-xs text-tinta-2">
                          <span className="numero">{r.dia_inteiro ? t('crm.ficha.reuniaoAgenda.diaInteiro') : hora(r.inicio)}</span>·<TextoCortado>{t('tipoAtividade.reuniao')}</TextoCortado>
                        </span>
                      </div>
                      <Selo cor={COR[status]}>{t(`crm.atividades.status.${status}`)}</Selo>
                    </li>
                  );
                }
                const a = item.a;
                return (
                <li key={a.id} className="flex items-center gap-3 rounded-xl border border-borda bg-sup-1 px-3 py-2.5" data-tarefa-atalho={a.id}>
                  {podeEditar && (
                    <input type="checkbox" className="size-4 shrink-0" checked={false} aria-label={t('crm.ficha.concluirTarefa', { titulo: a.titulo })} onChange={() => setConcluir(a)} data-concluir-atalho={a.id} />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                      <IconeTipoAtividade tipo={a.tipo} className="size-3.5" />
                      <Link to={caminhoDaTarefa(a.oportunidade_id, a.id)} onClick={aoFechar} title={textoLivre(a.titulo, a.parceiro_nome)} className="min-w-0 hover:underline" data-abrir-tarefa-atalho="">
                        <TextoCortado>{textoLivre(a.titulo, a.parceiro_nome)}</TextoCortado>
                      </Link>
                    </span>
                    <span className="flex min-w-0 items-center gap-1 text-xs text-tinta-2">
                      <span className="numero">{hora(a.data_hora)}</span>·
                      <Link to={caminhoDaTarefa(a.oportunidade_id)} onClick={aoFechar} title={textoLivre(a.oportunidade_titulo, a.parceiro_nome)} className="truncate text-acento-forte hover:underline">
                        {textoLivre(a.oportunidade_titulo, a.parceiro_nome)}
                      </Link>
                    </span>
                  </div>
                  <Selo cor={COR[statusDa(a)]}>{t(`crm.atividades.status.${statusDa(a)}`)}</Selo>
                </li>
                );
              })}
            </ul>
          )}
          {/* Lista de 02/11 (item 9): as tarefas excluídas do dia (de quem o atalho mostra: eu, ou todos com a caixa marcada), com Restaurar. */}
          {aberto && <TarefasExcluidasGeral params={paramsExcl} titulo={t(todas ? 'crm.excluidas.atalhoTodasTitulo' : 'crm.excluidas.atalhoTitulo')} vazio={t(todas ? 'crm.excluidas.atalhoTodasVazio' : 'crm.excluidas.atalhoVazio')} rotuloLista={t(todas ? 'crm.excluidas.atalhoTodasTitulo' : 'crm.excluidas.atalhoTitulo')} aoNavegar={aoFechar} />}
        </div>
      </Drawer>
      {concluir && (
        <DialogoConcluir
          atividades={[concluir]}
          aoFechar={() => setConcluir(null)}
          aoConcluir={({ ata }) => {
            if (workflows.data?.abrir_proxima ?? true) setProxima({ a: concluir, ata });
          }}
        />
      )}
      {proxima && <ProximaTarefa concluida={proxima.a} ata={proxima.ata} aoFechar={() => setProxima(null)} />}
      {criar && (
        <FormTarefa
          inicial={rascunhoInicial(s.usuario.id, { dataHora: juntarDataHora(ymd(dia), '09:00') })}
          aoFechar={() => {
            setCriar(false);
            void qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
          }}
        />
      )}
    </>
  );
}

/**
 * Botão de calendário "Atalho de tarefas" para o cabeçalho das páginas do CRM (Comercial, Empresas, Contatos e a ficha).
 * Publicado no `ModuloWeb.atalhoTarefas`; as telas de outro módulo usam `nucleo/atalho-tarefas`. Só com `pipeline.ver`.
 */
export function BotaoAtalhoTarefas() {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const [aberto, setAberto] = useState(false);
  if (!s.pode('pipeline.ver')) return null;
  return (
    <>
      <Botao icone={<CalendarDays className="size-4" />} aria-label={t('crm.atalho.titulo')} title={t('crm.atalho.titulo')} onClick={() => setAberto(true)} data-atalho-tarefas-botao="" />
      {aberto && <AtalhoTarefas aberto aoFechar={() => setAberto(false)} />}
    </>
  );
}
